/** Tab 12: System Access — create / unlink / disable / reset password */
import { useState } from 'react'
import {
  Alert, Button, Card, Form, Input, message, Modal, Popconfirm, Row, Col,
  Space, Switch, Tag, Tooltip, Typography,
} from 'antd'
import {
  DisconnectOutlined, KeyOutlined, LockOutlined, ReloadOutlined,
  UnlockOutlined, UserAddOutlined,
} from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { employeeUserApi } from '@/features/hr/enablementApi'

const { Text, Paragraph } = Typography

interface Props { detail: JoiningDetail }

// Cast helper — detail.employee is Record<string, unknown> from the API.
interface EmpAccessFields {
  user_id?: string | null
  user_login_required?: boolean
  system_access_notes?: string | null
}

export default function SystemAccessTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)
  // After create / reset, the temp password shows once. State holds it so
  // HR can copy before navigating away.
  const [tempPassword, setTempPassword] = useState<{
    username: string; password: string; reason: 'created' | 'reset'
  } | null>(null)
  // Unlink modal — collects optional reason + the "also deactivate" choice.
  const [unlinkModal, setUnlinkModal] = useState(false)
  const [unlinkForm] = Form.useForm<{ also_deactivate_user: boolean; reason: string }>()

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
      qc.invalidateQueries({ queryKey: ['employee-detail', detail.id] })
    },
  })

  const createMut = useMutation({
    mutationFn: () => employeeUserApi.createUserForEmployee(detail.id),
    onSuccess: (r) => {
      if (r.was_existing) {
        message.info(`User ${r.username} already linked — no change`)
      } else if (r.temp_password && r.username) {
        setTempPassword({ username: r.username, password: r.temp_password, reason: 'created' })
      }
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
      qc.invalidateQueries({ queryKey: ['employee-detail', detail.id] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const unlinkMut = useMutation({
    mutationFn: (body: { also_deactivate_user: boolean; reason?: string }) =>
      employeeUserApi.unlinkUser(detail.id, body),
    onSuccess: (r) => {
      message.success(
        r.user_deactivated
          ? 'User unlinked and deactivated'
          : 'User unlinked (account still active)',
      )
      setUnlinkModal(false)
      unlinkForm.resetFields()
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
      qc.invalidateQueries({ queryKey: ['employee-detail', detail.id] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Unlink failed'),
  })

  const accessMut = useMutation({
    mutationFn: (body: { is_active?: boolean | null; is_locked?: boolean | null; reason?: string }) =>
      employeeUserApi.setUserAccess(detail.id, body),
    onSuccess: (r) => {
      if (!r.changed) message.info('Already in that state — no change')
      else message.success('Access updated')
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
      qc.invalidateQueries({ queryKey: ['employee-detail', detail.id] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Update failed'),
  })

  const resetMut = useMutation({
    mutationFn: () => employeeUserApi.resetPassword(detail.id),
    onSuccess: (r) => {
      setTempPassword({ username: r.username, password: r.temp_password, reason: 'reset' })
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
      qc.invalidateQueries({ queryKey: ['employee-detail', detail.id] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Reset failed'),
  })

  const initial: Record<string, unknown> = { ...detail.employee }
  const empFields = detail.employee as unknown as EmpAccessFields
  const userId = empFields.user_id ?? null
  const isLinked = !!userId

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      <Card size="small" title="System Access" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="user_login_required" label="User Login Required" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="system_access_notes" label="System Access Notes">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>

        {/* Link / unlink panel */}
        {!isLinked ? (
          <Card size="small" type="inner" style={{ marginTop: 12, background: '#f5f5f5' }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text type="secondary">
                No ERP login linked to this employee. Click below to generate a
                username + temporary password automatically.
              </Text>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => createMut.mutate()}
                loading={createMut.isPending}
              >
                Create ERP Login
              </Button>
            </Space>
          </Card>
        ) : (
          <Card size="small" type="inner" style={{ marginTop: 12 }}>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <Space wrap>
                <Tag color="green">Linked</Tag>
                <Text>User ID: <code>{userId}</code></Text>
              </Space>

              <Space wrap>
                <Tooltip title="Generate a new temporary password. Clears any lock + activates the account.">
                  <Popconfirm
                    title="Reset password for this user?"
                    description="A new one-time password will be shown. The worker must change it on next login."
                    onConfirm={() => resetMut.mutate()}
                    okText="Reset"
                  >
                    <Button
                      icon={<KeyOutlined />}
                      loading={resetMut.isPending}
                    >
                      Reset Password
                    </Button>
                  </Popconfirm>
                </Tooltip>

                <Tooltip title="Lock the account — prevents login but keeps the link. Reversible.">
                  <Popconfirm
                    title="Lock this user account?"
                    description="They won't be able to log in. Reverse with Unlock."
                    onConfirm={() => accessMut.mutate({ is_locked: true })}
                    okText="Lock"
                  >
                    <Button
                      icon={<LockOutlined />}
                      loading={accessMut.isPending}
                    >
                      Lock Account
                    </Button>
                  </Popconfirm>
                </Tooltip>

                <Tooltip title="Unlock if previously locked. Sets is_locked=false.">
                  <Button
                    icon={<UnlockOutlined />}
                    onClick={() => accessMut.mutate({ is_locked: false, is_active: true })}
                    loading={accessMut.isPending}
                  >
                    Unlock / Re-enable
                  </Button>
                </Tooltip>

                <Tooltip title="Disconnect the employee from this user. Default also deactivates the account.">
                  <Button
                    danger
                    icon={<DisconnectOutlined />}
                    onClick={() => {
                      unlinkForm.setFieldsValue({ also_deactivate_user: true, reason: '' })
                      setUnlinkModal(true)
                    }}
                  >
                    Unlink / Cancel Access
                  </Button>
                </Tooltip>
              </Space>

              <Text type="secondary" style={{ fontSize: 12 }}>
                <strong>Reset</strong> = new password.{' '}
                <strong>Lock</strong> = temporary block (e.g. suspension).{' '}
                <strong>Unlink</strong> = break the connection (worker left,
                wrong user was linked, or permanent revoke).
              </Text>
            </Space>
          </Card>
        )}
      </Card>

      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>

      {/* Temp password modal — shown once after create or reset */}
      <Modal
        open={!!tempPassword}
        title={tempPassword?.reason === 'created' ? 'ERP login created' : 'Password reset'}
        onCancel={() => setTempPassword(null)}
        footer={[
          <Button key="done" type="primary" onClick={() => setTempPassword(null)}>
            I have copied the password
          </Button>,
        ]}
        closable={false}
        maskClosable={false}
      >
        {tempPassword && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              icon={<ReloadOutlined />}
              message="This password will not be shown again. Copy it now and share securely with the worker."
            />
            <div style={{
              padding: 12, background: '#fafafa',
              border: '1px solid #d9d9d9', borderRadius: 4,
            }}>
              <Paragraph copyable={{ text: tempPassword.username }} style={{ margin: 0 }}>
                <Text strong>Username:</Text> <code>{tempPassword.username}</code>
              </Paragraph>
              <Paragraph copyable={{ text: tempPassword.password }} style={{ margin: 0 }}>
                <Text strong>Temporary password:</Text> <code>{tempPassword.password}</code>
              </Paragraph>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              The worker should change this on first login.
            </Text>
          </Space>
        )}
      </Modal>

      {/* Unlink modal — confirms + collects reason */}
      <Modal
        open={unlinkModal}
        title="Unlink user from this employee"
        onCancel={() => { setUnlinkModal(false); unlinkForm.resetFields() }}
        onOk={() => unlinkForm.validateFields().then((v) =>
          unlinkMut.mutate({ also_deactivate_user: v.also_deactivate_user, reason: v.reason || undefined })
        )}
        okText="Unlink"
        okButtonProps={{ danger: true, loading: unlinkMut.isPending }}
      >
        <Form form={unlinkForm} layout="vertical">
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="This breaks the employee↔user connection."
            description="The user account is preserved for audit. With 'also deactivate' checked (default), the account can't be used to log in until you re-link or re-enable it."
          />
          <Form.Item
            name="also_deactivate_user"
            valuePropName="checked"
            label="Also deactivate the user account"
            tooltip="Uncheck only if HR linked the wrong user and wants to leave that account usable for someone else."
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason (optional but recommended)"
          >
            <Input.TextArea
              rows={2}
              placeholder="e.g. Worker resigned 2026-05-25. Returned ID card."
            />
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  )
}
